import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Search, Send, AlertTriangle, Sparkles } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { EmailTabNav } from "@/components/admin/email/EmailTabNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { EMAIL_CATEGORY_CONFIG } from "@/types/email";
import type { EmailTemplateConfig, EmailCategory } from "@/types/email";

interface Recipient {
  email: string;
  name: string;
  type: "student" | "partner" | "external";
  meta?: Record<string, unknown>;
}

const STEPS = ["Template", "Destinatário", "Variáveis", "Confirmar"];

export default function AdminEmailSend() {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateConfig | null>(null);
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [variables, setVariables] = useState<Record<string, unknown>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [freeEmail, setFreeEmail] = useState("");
  const [freeName, setFreeName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [skipCooldown, setSkipCooldown] = useState(false);
  const [autoFilledKeys, setAutoFilledKeys] = useState<Set<string>>(new Set());
  const [recipientTypeFilter, setRecipientTypeFilter] = useState<"all" | "student" | "partner">("all");

  // Templates
  const { data: templates } = useQuery({
    queryKey: ["email-templates-config"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates_config")
        .select("*")
        .eq("is_implemented", true)
        .order("category");
      if (error) throw error;
      return data as EmailTemplateConfig[];
    },
  });

  // Search students
  const { data: students } = useQuery({
    queryKey: ["email-send-students", searchTerm],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email, city, state, objective, bio, phone, profile_status, cep")
        .eq("role", "student")
        .or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        .limit(10);
      if (error) throw error;
      return data;
    },
    enabled: searchTerm.length >= 2,
  });

  // Search partners (from partner_orgs directly)
  const { data: partners } = useQuery({
    queryKey: ["email-send-partners", searchTerm],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_orgs")
        .select("id, name, contact_name, contact_email, contact_phone, industry, city, state, website")
        .or(`name.ilike.%${searchTerm}%,contact_name.ilike.%${searchTerm}%,contact_email.ilike.%${searchTerm}%`)
        .limit(10);
      if (error) throw error;
      return data;
    },
    enabled: searchTerm.length >= 2,
  });

  // Smart fill variables when recipient selected
  useEffect(() => {
    if (!recipient || !selectedTemplate?.variables_schema) return;
    const schema = selectedTemplate.variables_schema;
    const filled: Record<string, unknown> = { ...variables };
    const filledKeys = new Set<string>();

    if (recipient.type === "student" && recipient.meta) {
      const map: Record<string, unknown> = {
        studentName: recipient.name,
        studentFirstName: recipient.name?.split(" ")[0],
        studentLastInitial: recipient.name?.split(" ").pop()?.[0],
        studentEmail: recipient.email,
        studentCity: recipient.meta.city,
        studentState: recipient.meta.state,
        studentObjective: recipient.meta.objective,
      };
      for (const [k, v] of Object.entries(map)) {
        if (k in schema && v) { filled[k] = v; filledKeys.add(k); }
      }
    }
    if (recipient.type === "partner" && recipient.meta) {
      const map: Record<string, unknown> = {
        contactName: recipient.name,
        partnerUserName: recipient.name,
        contactEmail: recipient.email,
        orgName: recipient.meta.orgName,
        companyName: recipient.meta.orgName,
      };
      for (const [k, v] of Object.entries(map)) {
        if (k in schema && v) { filled[k] = v; filledKeys.add(k); }
      }
    }

    setVariables(filled);
    setAutoFilledKeys(filledKeys);
  }, [recipient, selectedTemplate]);

  // Job posts for jobTitle variable (EM14 AC3)
  const needsJobTitle = selectedTemplate?.variables_schema && "jobTitle" in selectedTemplate.variables_schema;
  const { data: jobPosts } = useQuery({
    queryKey: ["email-send-job-posts"],
    queryFn: async () => {
      const { data } = await supabase
        .from("job_posts")
        .select("id, title, status")
        .in("status", ["publicada", "ativa"])
        .order("created_at", { ascending: false })
        .limit(50);
      return data ?? [];
    },
    enabled: !!needsJobTitle && !!recipient,
  });

  // Preview HTML
  const { data: previewHtml } = useQuery({
    queryKey: ["email-preview-render", selectedTemplate?.template_key, variables],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("email-preview", {
        body: { templateKey: selectedTemplate!.template_key, variables },
      });
      if (error) throw error;
      return data?.html as string | null;
    },
    enabled: !!selectedTemplate && step >= 2,
  });

  // Send
  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate || !recipient) throw new Error("Dados incompletos");
      const { data, error } = await supabase.functions.invoke("send-manual-email", {
        body: {
          templateKey: selectedTemplate.template_key,
          recipientEmail: recipient.email,
          recipientName: recipient.name,
          recipientType: recipient.type,
          variables,
          skipCooldown,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: `Email enviado para ${recipient?.email}`,
        description: "Clique em 'Log de Envios' nas tabs para ver o registro.",
      });
      setShowConfirmDialog(false);
      setStep(0);
      setSelectedTemplate(null);
      setRecipient(null);
      setVariables({});
      setConfirmed(false);
    },
    onError: (err) => {
      toast({ title: "Erro ao enviar", description: String(err), variant: "destructive" });
    },
  });

  // Group templates by category
  const grouped = (templates ?? []).reduce((acc, t) => {
    const cat = t.category as EmailCategory;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {} as Record<EmailCategory, EmailTemplateConfig[]>);

  const canAdvance = () => {
    if (step === 0) return !!selectedTemplate;
    if (step === 1) return !!recipient;
    if (step === 2) return true;
    return false;
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <EmailTabNav />

      <h1 className="text-2xl font-bold text-foreground mb-2">Disparo Manual</h1>
      <p className="text-sm text-muted-foreground mb-8">Envie um email manualmente para um destinatário específico</p>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 max-w-2xl mx-auto">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
              i < step ? "bg-primary text-primary-foreground" :
              i === step ? "bg-primary text-primary-foreground" :
              "bg-muted text-muted-foreground"
            }`}>
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${i <= step ? "text-foreground" : "text-muted-foreground"}`}>
              {label}
            </span>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-primary" : "bg-muted"}`} />}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="max-w-4xl mx-auto">
        {/* Step 0: Select Template */}
        {step === 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-6 border border-border">
            <h2 className="text-lg font-semibold mb-2">Qual email você quer enviar?</h2>
            {/* Recipient type filter */}
            <div className="flex gap-1.5 mb-4">
              {([
                { value: "all" as const, label: "Todos" },
                { value: "student" as const, label: "Para Aluno" },
                { value: "partner" as const, label: "Para Empresa" },
              ]).map((f) => (
                <button
                  key={f.value}
                  onClick={() => setRecipientTypeFilter(f.value)}
                  className={`text-[11px] font-medium px-3 py-1.5 rounded-full transition-colors ${
                    recipientTypeFilter === f.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground border border-border hover:bg-muted"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="space-y-4">
              {Object.entries(grouped).map(([cat, items]) => {
                const filteredItems = recipientTypeFilter === "all"
                  ? items
                  : items.filter((t) =>
                      t.recipient_type === recipientTypeFilter || t.recipient_type === "both"
                    );
                if (filteredItems.length === 0) return null;
                return (
                <div key={cat}>
                  <p className="text-xs font-bold uppercase text-muted-foreground mb-2">
                    {EMAIL_CATEGORY_CONFIG[cat as EmailCategory]?.label}
                  </p>
                  <div className="space-y-1">
                    {filteredItems.map((t) => (
                      <button
                        key={t.template_key}
                        onClick={() => setSelectedTemplate(t)}
                        disabled={!t.is_active && !selectedTemplate}
                        className={`w-full text-left p-3 rounded-xl transition-colors ${
                          selectedTemplate?.template_key === t.template_key
                            ? "bg-primary/10 border border-primary/30"
                            : "hover:bg-muted border border-transparent"
                        } ${!t.is_active ? "opacity-50" : ""}`}
                      >
                        <p className="text-sm font-medium">
                          {t.display_name}
                          {!t.is_active && <span className="text-xs text-amber-600 ml-2">(Desativado)</span>}
                          <span className="text-[10px] text-muted-foreground ml-2">
                            {t.recipient_type === "both" ? "Aluno + Empresa" : t.recipient_type === "student" ? "Aluno" : "Empresa"}
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{t.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              );
              })}
            </div>
          </motion.div>
        )}

        {/* Step 1: Select Recipient */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-6 border border-border">
            <h2 className="text-lg font-semibold mb-4">Para quem?</h2>
            <Tabs defaultValue="student">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="student">Aluno</TabsTrigger>
                <TabsTrigger value="partner">Empresa</TabsTrigger>
                <TabsTrigger value="free">Email livre</TabsTrigger>
              </TabsList>

              <TabsContent value="student" className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Buscar aluno por nome ou email..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                {students?.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setRecipient({
                        email: s.email!, name: s.full_name || s.email!, type: "student",
                        meta: { city: s.city, state: s.state, objective: (s as any).objective, bio: (s as any).bio, phone: (s as any).phone, profileStatus: (s as any).profile_status },
                      });
                      setSearchTerm("");
                    }}
                    className="w-full text-left p-3 rounded-xl hover:bg-muted flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                      {(s.full_name || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{s.full_name || "Sem nome"}</p>
                      <p className="text-xs text-muted-foreground">{s.email} {s.city && `• ${s.city}/${s.state}`}</p>
                    </div>
                  </button>
                ))}
              </TabsContent>

              <TabsContent value="partner" className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Buscar empresa por nome ou email..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                {partners?.map((p: any) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setRecipient({
                        email: p.contact_email, name: p.contact_name || p.name, type: "partner",
                        meta: { orgName: p.name, industry: p.industry, city: p.city, state: p.state, website: p.website, phone: p.contact_phone },
                      });
                      setSearchTerm("");
                    }}
                    className="w-full text-left p-3 rounded-xl hover:bg-muted flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                      {(p.contact_name || p.name || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.contact_email} {p.industry && `• ${p.industry}`} {p.city && `• ${p.city}/${p.state}`}</p>
                    </div>
                  </button>
                ))}
              </TabsContent>

              <TabsContent value="free" className="space-y-3">
                <Input placeholder="Email do destinatário" type="email" value={freeEmail} onChange={(e) => setFreeEmail(e.target.value)} />
                <Input placeholder="Nome (opcional)" value={freeName} onChange={(e) => setFreeName(e.target.value)} />
                <Button
                  variant="outline"
                  disabled={!freeEmail}
                  onClick={() => setRecipient({ email: freeEmail, name: freeName || freeEmail, type: "external" })}
                >
                  Confirmar destinatário
                </Button>
              </TabsContent>
            </Tabs>

            {recipient && (
              <div className="mt-4 bg-muted/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${recipient.type === "partner" ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"}`}>
                      {recipient.name[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{recipient.name}</p>
                      <p className="text-xs text-muted-foreground">{recipient.email}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setRecipient(null)}>Trocar</Button>
                </div>
                {/* Expanded data */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                  {recipient.type === "student" && (
                    <>
                      {recipient.meta?.city && <div><p className="text-[10px] uppercase text-muted-foreground">Cidade</p><p className="text-xs font-medium">{String(recipient.meta.city)}/{String(recipient.meta.state)}</p></div>}
                      {recipient.meta?.phone && <div><p className="text-[10px] uppercase text-muted-foreground">Telefone</p><p className="text-xs font-medium">{String(recipient.meta.phone)}</p></div>}
                      {recipient.meta?.profileStatus && <div><p className="text-[10px] uppercase text-muted-foreground">Status</p><p className="text-xs font-medium">{String(recipient.meta.profileStatus)}</p></div>}
                      {recipient.meta?.objective && <div className="col-span-2"><p className="text-[10px] uppercase text-muted-foreground">Objetivo</p><p className="text-xs font-medium">{String(recipient.meta.objective)}</p></div>}
                      {recipient.meta?.bio && <div className="col-span-2"><p className="text-[10px] uppercase text-muted-foreground">Bio</p><p className="text-xs font-medium line-clamp-2">{String(recipient.meta.bio)}</p></div>}
                    </>
                  )}
                  {recipient.type === "partner" && (
                    <>
                      {recipient.meta?.orgName && <div><p className="text-[10px] uppercase text-muted-foreground">Empresa</p><p className="text-xs font-medium">{String(recipient.meta.orgName)}</p></div>}
                      {recipient.meta?.industry && <div><p className="text-[10px] uppercase text-muted-foreground">Setor</p><p className="text-xs font-medium">{String(recipient.meta.industry)}</p></div>}
                      {recipient.meta?.city && <div><p className="text-[10px] uppercase text-muted-foreground">Cidade</p><p className="text-xs font-medium">{String(recipient.meta.city)}/{String(recipient.meta.state)}</p></div>}
                      {recipient.meta?.phone && <div><p className="text-[10px] uppercase text-muted-foreground">Telefone</p><p className="text-xs font-medium">{String(recipient.meta.phone)}</p></div>}
                      {recipient.meta?.website && <div className="col-span-2"><p className="text-[10px] uppercase text-muted-foreground">Website</p><p className="text-xs font-medium">{String(recipient.meta.website)}</p></div>}
                    </>
                  )}
                  {recipient.type === "external" && (
                    <div className="col-span-2"><p className="text-[10px] uppercase text-muted-foreground">Tipo</p><p className="text-xs font-medium">Destinatário externo</p></div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Step 2: Variables */}
        {step === 2 && selectedTemplate && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-6 border border-border">
            <h2 className="text-lg font-semibold mb-4">Dados do email</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                {selectedTemplate.variables_schema && Object.entries(selectedTemplate.variables_schema).map(([name, schema]) => (
                  <div key={name} className="space-y-1.5">
                    <label className="text-sm font-medium flex items-center gap-1.5">
                      {(schema as any).label || name}
                      {autoFilledKeys.has(name) && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-primary">
                          <Sparkles className="w-3 h-3" /> auto
                        </span>
                      )}
                    </label>
                    {/* EM14 AC3: dropdown de vagas para jobTitle */}
                    {name === "jobTitle" && jobPosts && jobPosts.length > 0 ? (
                      <select
                        value={String(variables[name] ?? "")}
                        onChange={(e) => { setVariables((prev) => ({ ...prev, [name]: e.target.value })); setAutoFilledKeys((prev) => { const n = new Set(prev); n.delete(name); return n; }); }}
                        className={`h-10 w-full rounded-xl border bg-background px-3 text-sm ${autoFilledKeys.has(name) ? "border-primary/30 bg-primary/5" : "border-border"}`}
                      >
                        <option value="">Selecionar vaga...</option>
                        {jobPosts.map((j) => (
                          <option key={j.id} value={j.title}>{j.title}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        value={String(variables[name] ?? "")}
                        onChange={(e) => { setVariables((prev) => ({ ...prev, [name]: e.target.value })); setAutoFilledKeys((prev) => { const n = new Set(prev); n.delete(name); return n; }); }}
                        className={autoFilledKeys.has(name) ? "border-primary/30 bg-primary/5" : ""}
                      />
                    )}
                  </div>
                ))}
                {(!selectedTemplate.variables_schema || Object.keys(selectedTemplate.variables_schema).length === 0) && (
                  <p className="text-sm text-muted-foreground">Este template não possui variáveis editáveis.</p>
                )}
                {/* Cooldown checkbox */}
                <label className="flex items-center gap-3 pt-2 cursor-pointer">
                  <Checkbox checked={skipCooldown} onCheckedChange={(v) => setSkipCooldown(!!v)} />
                  <div>
                    <span className="text-sm font-medium">Ignorar cooldown</span>
                    <p className="text-xs text-muted-foreground">Envia mesmo se o destinatário recebeu este tipo recentemente</p>
                  </div>
                </label>
              </div>
              <div>
                {previewHtml ? (
                  <iframe srcDoc={previewHtml} sandbox="allow-same-origin" className="w-full h-[400px] bg-white border border-border rounded-xl" title="Preview" />
                ) : (
                  <div className="w-full h-[400px] bg-muted/30 rounded-xl flex items-center justify-center text-muted-foreground text-sm">
                    Carregando preview...
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
          </Button>
          {step < 2 ? (
            <Button disabled={!canAdvance()} onClick={() => setStep(step + 1)}>
              Continuar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={() => { setShowConfirmDialog(true); setConfirmed(false); }}>
              Revisar e Enviar <Send className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Confirm Dialog (Step 3) */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirmar envio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-xl space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Template:</span>
                <span className="font-semibold">{selectedTemplate?.display_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Para:</span>
                <span className="font-semibold">{recipient?.name} ({recipient?.email})</span>
              </div>
            </div>

            {previewHtml && (
              <iframe srcDoc={previewHtml} sandbox="allow-same-origin" className="w-full h-[250px] bg-white border border-border rounded-xl" title="Preview final" />
            )}

            {!selectedTemplate?.is_active && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800">Este template está desativado. O email será enviado mesmo assim pois é disparo manual.</p>
              </div>
            )}
            {recipient?.type === "external" && (
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-blue-600 shrink-0" />
                <p className="text-sm text-blue-800">Este email será enviado para um destinatário externo.</p>
              </div>
            )}

            <label className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-xl cursor-pointer">
              <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" />
              <span className="text-sm font-medium">Confirmo que os dados estão corretos e quero enviar este email.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfirmDialog(false)}>Voltar</Button>
            <Button disabled={!confirmed || sendMutation.isPending} onClick={() => sendMutation.mutate()}>
              <Send className="w-4 h-4 mr-2" />
              {sendMutation.isPending ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
